import React, { useEffect, useState } from 'react';
import {
  Plus,
  Pencil,
  DollarSign,
  Search,
  BookOpen,
  Receipt,
  ArrowRightLeft,
  FileText,
  SlidersHorizontal,
  Calculator,
  Repeat,
} from 'lucide-react';
import { billingService } from '../../api/services/billing.service';
import {
  CreditAccountDTO,
  CreateCreditAccountRequest,
  BillingType,
  billingTypeLabels,
  BILLING_CURRENCIES,
  CreditSource,
  creditSourceLabels,
  formatMoney,
} from '../../types/billing';
import { SkeletonTable } from '../../components/Common/Skeleton';
import { useAuth } from 'hooks/useAuth';
import { canCloseBooks } from './closeBooksRules';
import LedgerModal from './LedgerModal';
import InvoiceUsageModal from './InvoiceUsageModal';
import ConvertToPrepaidModal from './ConvertToPrepaidModal';
import BillingProfileModal from './BillingProfileModal';
import { canEditBillingProfile } from './billingProfileRules';
import AccountControlsModal from './AccountControlsModal';
import AutoRechargeModal from './AutoRechargeModal';
import { canConfigureAutoRecharge } from './autoRechargeRules';
import InvoicePreviewModal from './InvoicePreviewModal';
import { canEditCreditControls } from './creditControlRules';
import { DEFAULT_GRANT_PRIORITY, addCreditsBody } from './creditGrantRules';
import { formatServerUtcAsIst } from './closeBooksRules';

const CreditAccounts: React.FC = () => {
  const [accounts, setAccounts] = useState<CreditAccountDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState<CreditAccountDTO | null>(null);
  const [showAddCreditsModal, setShowAddCreditsModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<CreditAccountDTO | null>(null);
  const [ledgerAccount, setLedgerAccount] = useState<CreditAccountDTO | null>(null);
  const [invoicingAccount, setInvoicingAccount] = useState<CreditAccountDTO | null>(null);
  const [convertingAccount, setConvertingAccount] = useState<CreditAccountDTO | null>(null);
  const [profileAccount, setProfileAccount] = useState<CreditAccountDTO | null>(null);
  const [controlsAccount, setControlsAccount] = useState<CreditAccountDTO | null>(null);
  const [autoRechargeAccount, setAutoRechargeAccount] = useState<CreditAccountDTO | null>(null);
  const [previewAccount, setPreviewAccount] = useState<CreditAccountDTO | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const { user, currentOrganization } = useAuth();
  const isBillingAdmin = canCloseBooks((currentOrganization as any)?.role || (user as any)?.role);

  const [formData, setFormData] = useState<CreateCreditAccountRequest>({
    organizationId: '',
    billingType: BillingType.POSTPAID,
    initialCredits: 0,
    lowBalanceThreshold: 100,
    currency: 'INR',
  });

  const [addCreditsAmount, setAddCreditsAmount] = useState<number>(0);
  const [addCreditsDescription, setAddCreditsDescription] = useState('');
  const [addCreditsSource, setAddCreditsSource] = useState<CreditSource>('PURCHASE');
  /** IST `datetime-local`; blank = never expires. */
  const [addCreditsExpires, setAddCreditsExpires] = useState('');
  const [addCreditsPriority, setAddCreditsPriority] = useState('');
  const [addCreditsAdvanced, setAddCreditsAdvanced] = useState(false);
  const [addCreditsError, setAddCreditsError] = useState<string | null>(null);

  useEffect(() => {
    loadAccounts();
  }, [page]);

  const loadAccounts = async () => {
    setLoading(true);
    const response = await billingService.getCreditAccounts({ page, size: 20 });
    if (response.success && response.data) {
      setAccounts(response.data.content);
      setTotalPages(response.data.totalPages);
      setTotalElements(response.data.totalElements);
    }
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let response;
    if (editingAccount) {
      response = await billingService.updateCreditAccount(editingAccount.id, formData);
    } else {
      response = await billingService.createCreditAccount(formData);
    }

    if (response.success) {
      setShowModal(false);
      setEditingAccount(null);
      resetForm();
      loadAccounts();
    }
  };

  const resetAddCredits = () => {
    setShowAddCreditsModal(false);
    setSelectedAccount(null);
    setAddCreditsAmount(0);
    setAddCreditsDescription('');
    setAddCreditsSource('PURCHASE');
    setAddCreditsExpires('');
    setAddCreditsPriority('');
    setAddCreditsAdvanced(false);
    setAddCreditsError(null);
  };

  const handleAddCredits = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAccount) return;
    setAddCreditsError(null);
    const built = addCreditsBody({
      organizationId: selectedAccount.organizationId,
      amount: addCreditsAmount,
      description: addCreditsDescription,
      source: addCreditsSource,
      expiresIst: addCreditsExpires,
      priority: addCreditsPriority,
    });
    if ('error' in built) {
      setAddCreditsError(built.error);
      return;
    }

    const response = await billingService.addCredits(built.body);

    if (response.success) {
      const name = selectedAccount.organizationName || selectedAccount.organizationId;
      const expiry = response.data?.grantExpiresAt
        ? ` Expires ${formatServerUtcAsIst(response.data.grantExpiresAt)} IST.`
        : '';
      setNotice(`Added ${formatMoney(built.body.amount, selectedAccount.currency || 'INR')} to ${name}.${expiry}`);
      resetAddCredits();
      loadAccounts();
    } else {
      // Keep the form as typed so a refused term can be fixed.
      setAddCreditsError(response.error || 'Failed to add credits');
    }
  };

  const resetForm = () => {
    setFormData({
      organizationId: '',
      billingType: BillingType.POSTPAID,
      initialCredits: 0,
      lowBalanceThreshold: 100,
      currency: 'INR',
    });
  };

  const openEditModal = (account: CreditAccountDTO) => {
    setEditingAccount(account);
    setFormData({
      organizationId: account.organizationId,
      billingType: account.billingType,
      creditLimit: account.creditLimit,
      lowBalanceThreshold: account.lowBalanceThreshold,
      autoRechargeEnabled: account.autoRechargeEnabled,
      autoRechargeAmount: account.autoRechargeAmount,
      autoRechargeThreshold: account.autoRechargeThreshold,
      currency: account.currency,
      billingEmail: account.billingEmail,
      billingAddress: account.billingAddress,
      taxId: account.taxId,
    });
    setShowModal(true);
  };

  const openAddCreditsModal = (account: CreditAccountDTO) => {
    setSelectedAccount(account);
    setShowAddCreditsModal(true);
  };

  /** In the account's OWN currency: every live account is INR. */
  const formatCurrency = (value: number | undefined, currency?: string) => formatMoney(value ?? 0, currency || 'INR');

  const getBalanceColor = (account: CreditAccountDTO) => {
    if (account.creditBalance <= 0) return 'text-red-600';
    if (account.lowBalanceThreshold && account.creditBalance <= account.lowBalanceThreshold) {
      return 'text-orange-600';
    }
    return 'text-green-600';
  };

  if (loading && accounts.length === 0) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Credit Accounts</h1>
        <SkeletonTable rows={10} columns={6} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Credit Accounts</h1>
        <button
          onClick={() => {
            resetForm();
            setEditingAccount(null);
            setShowModal(true);
          }}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Account
        </button>
      </div>

      {notice && (
        <div className="rounded-lg border p-3 text-sm flex justify-between gap-4 bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300">
          <span>{notice}</span>
          <button onClick={() => setNotice(null)} aria-label="Dismiss">&times;</button>
        </div>
      )}

      {/* Accounts Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Organization
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Billing Type
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Balance
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Credit Limit
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {accounts.map((account) => (
                <tr key={account.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {account.organizationName || account.organizationId}
                      </div>
                      {account.billingEmail && (
                        <div className="text-sm text-gray-500 dark:text-gray-400">{account.billingEmail}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        account.billingType === BillingType.PREPAID
                          ? 'bg-green-100 text-green-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {billingTypeLabels[account.billingType]}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <span className={`text-sm font-medium ${getBalanceColor(account)}`}>
                      {formatCurrency(account.creditBalance, account.currency)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500 dark:text-gray-400">
                    {account.creditLimit ? formatCurrency(account.creditLimit, account.currency) : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        account.active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {account.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setProfileAccount(account)}
                        className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-700 rounded"
                        title="Billing profile"
                      >
                        <FileText className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setLedgerAccount(account)}
                        className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-700 rounded"
                        title="Ledger"
                      >
                        <BookOpen className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setControlsAccount(account)}
                        className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-700 rounded"
                        title="Account controls"
                      >
                        <SlidersHorizontal className="h-4 w-4" />
                      </button>
                      {account.billingType === BillingType.PREPAID && (
                        <button
                          onClick={() => setAutoRechargeAccount(account)}
                          className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-700 rounded"
                          title="Auto-recharge"
                        >
                          <Repeat className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => setPreviewAccount(account)}
                        className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-700 rounded"
                        title="Invoice so far"
                      >
                        <Calculator className="h-4 w-4" />
                      </button>
                      {isBillingAdmin && account.billingType === BillingType.POSTPAID && (
                        <>
                          <button
                            onClick={() => setInvoicingAccount(account)}
                            className="p-2 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded"
                            title="Invoice usage"
                          >
                            <Receipt className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setConvertingAccount(account)}
                            className="p-2 text-orange-600 hover:text-orange-800 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded"
                            title="Convert to prepaid"
                          >
                            <ArrowRightLeft className="h-4 w-4" />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => openAddCreditsModal(account)}
                        className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded"
                        title="Add Credits"
                      >
                        <DollarSign className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => openEditModal(account)}
                        className="p-2 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded"
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="bg-white dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700 sm:px-6">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Showing {accounts.length} of {totalElements} accounts
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm disabled:opacity-50 dark:text-gray-300"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm disabled:opacity-50 dark:text-gray-300"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4 dark:text-white">
              {editingAccount ? 'Edit Credit Account' : 'Create Credit Account'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!editingAccount && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Organization ID *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.organizationId}
                    onChange={(e) =>
                      setFormData({ ...formData, organizationId: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Billing Type *
                </label>
                <select
                  value={formData.billingType}
                  // An existing account changes type only through Convert to prepaid, which
                  // refuses while postpaid usage is uninvoiced or unpaid. A plain edit would not.
                  disabled={!!editingAccount}
                  title={editingAccount ? 'Use Convert to prepaid on the account row' : undefined}
                  onChange={(e) =>
                    setFormData({ ...formData, billingType: e.target.value as BillingType })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {Object.entries(billingTypeLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Currency
                </label>
                <select
                  value={formData.currency || 'INR'}
                  disabled={!!editingAccount}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-60"
                >
                  {BILLING_CURRENCIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              {!editingAccount && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Initial Credits
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.initialCredits || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, initialCredits: parseFloat(e.target.value) })
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              )}

              {formData.billingType === BillingType.POSTPAID && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Credit Limit
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.creditLimit || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, creditLimit: parseFloat(e.target.value) })
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Low Balance Threshold
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.lowBalanceThreshold || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      lowBalanceThreshold: parseFloat(e.target.value),
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Billing Email
                </label>
                <input
                  type="email"
                  value={formData.billingEmail || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, billingEmail: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tax ID
                </label>
                <input
                  type="text"
                  value={formData.taxId || ''}
                  onChange={(e) => setFormData({ ...formData, taxId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingAccount(null);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="tl-btn tl-btn-primary"
                >
                  {editingAccount ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Credits Modal */}
      {showAddCreditsModal && selectedAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4 dark:text-white">Add Credits</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Adding credits to:{' '}
              <span className="font-medium text-gray-900 dark:text-white">
                {selectedAccount.organizationName || selectedAccount.organizationId}
              </span>
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Current balance:{' '}
              <span className={`font-medium ${getBalanceColor(selectedAccount)}`}>
                {formatCurrency(selectedAccount.creditBalance, selectedAccount.currency)}
              </span>
            </p>
            <form onSubmit={handleAddCredits} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Amount *
                </label>
                <input
                  type="number"
                  step="0.01"
                  required
                  min="0.01"
                  value={addCreditsAmount || ''}
                  onChange={(e) => setAddCreditsAmount(parseFloat(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={addCreditsDescription}
                  onChange={(e) => setAddCreditsDescription(e.target.value)}
                  placeholder="e.g., Credit purchase, Manual adjustment"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="ac-source" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Source
                </label>
                <select
                  id="ac-source"
                  value={addCreditsSource}
                  onChange={(e) => setAddCreditsSource(e.target.value as CreditSource)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {(Object.keys(creditSourceLabels) as CreditSource[]).map((s) => (
                    <option key={s} value={s}>{creditSourceLabels[s]}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="ac-expires" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Expires (IST, optional)
                </label>
                <input
                  id="ac-expires"
                  type="datetime-local"
                  value={addCreditsExpires}
                  onChange={(e) => setAddCreditsExpires(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Blank: the credit never expires.</p>
              </div>
              <div>
                <button
                  type="button"
                  onClick={() => setAddCreditsAdvanced((v) => !v)}
                  className="text-sm text-indigo-600 hover:text-indigo-800 dark:text-indigo-400"
                >
                  {addCreditsAdvanced ? 'Hide advanced' : 'Advanced'}
                </button>
                {addCreditsAdvanced && (
                  <div className="mt-2">
                    <label htmlFor="ac-priority" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Priority
                    </label>
                    <input
                      id="ac-priority"
                      type="number"
                      min="0"
                      step="1"
                      placeholder={String(DEFAULT_GRANT_PRIORITY)}
                      value={addCreditsPriority}
                      onChange={(e) => setAddCreditsPriority(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Lower is drawn first (default {DEFAULT_GRANT_PRIORITY}).
                    </p>
                  </div>
                )}
              </div>
              {addCreditsError && <p className="text-sm text-red-600">{addCreditsError}</p>}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={resetAddCredits}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  Add Credits
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {ledgerAccount && (
        <LedgerModal
          account={ledgerAccount}
          canRefund={canEditCreditControls(
            (currentOrganization as any)?.role || (user as any)?.role,
            ledgerAccount.organizationId,
            currentOrganization?.id,
          )}
          onClose={() => setLedgerAccount(null)}
        />
      )}

      {controlsAccount && (
        <AccountControlsModal
          account={controlsAccount}
          editable={canEditCreditControls(
            (currentOrganization as any)?.role || (user as any)?.role,
            controlsAccount.organizationId,
            currentOrganization?.id,
          )}
          onClose={() => {
            setControlsAccount(null);
            loadAccounts();
          }}
        />
      )}

      {autoRechargeAccount && (
        <AutoRechargeModal
          organizationId={autoRechargeAccount.organizationId}
          organizationName={autoRechargeAccount.organizationName}
          editable={canConfigureAutoRecharge((currentOrganization as any)?.role || (user as any)?.role)}
          onClose={() => {
            setAutoRechargeAccount(null);
            loadAccounts();
          }}
        />
      )}

      {previewAccount && <InvoicePreviewModal account={previewAccount} onClose={() => setPreviewAccount(null)} />}

      {invoicingAccount && (
        <InvoiceUsageModal
          organizationId={invoicingAccount.organizationId}
          onClose={() => setInvoicingAccount(null)}
          onDone={(text) => {
            setInvoicingAccount(null);
            setNotice(`${text} It is under Billing → Invoices.`);
          }}
        />
      )}

      {profileAccount && (
        <BillingProfileModal
          organizationId={profileAccount.organizationId}
          organizationName={profileAccount.organizationName}
          editable={canEditBillingProfile(
            (currentOrganization as any)?.role || (user as any)?.role,
            profileAccount.organizationId,
            currentOrganization?.id,
          )}
          onClose={() => setProfileAccount(null)}
          onSaved={(text) => {
            setProfileAccount(null);
            setNotice(text);
          }}
        />
      )}

      {convertingAccount && (
        <ConvertToPrepaidModal
          account={convertingAccount}
          onClose={() => setConvertingAccount(null)}
          onDone={(text) => {
            setConvertingAccount(null);
            setNotice(text);
            loadAccounts();
          }}
        />
      )}
    </div>
  );
};

export default CreditAccounts;
